"use client";

import * as React from "react";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Calendar, Users, UserPlus, CreditCard, Settings, Menu, Bell, MoreHorizontal, CheckCircle, XCircle, AlertCircle, Edit, Plus, Phone, Mail, DollarSign, Filter, Eye, MessageSquare } from "lucide-react";

// Mock data
const mockBookings = [{
  id: "1",
  client: "Sarah Johnson",
  service: "Hair Cut & Style",
  date: "2024-01-15",
  time: "10:00 AM",
  status: "confirmed" as const,
  payment: "paid" as const,
  amount: 85
}, {
  id: "2",
  client: "Michael Chen",
  service: "Color Treatment",
  date: "2024-01-15",
  time: "2:00 PM",
  status: "completed" as const,
  payment: "paid" as const,
  amount: 150
}, {
  id: "3",
  client: "Emma Wilson",
  service: "Manicure",
  date: "2024-01-16",
  time: "11:30 AM",
  status: "cancelled" as const,
  payment: "refunded" as const,
  amount: 45
}, {
  id: "4",
  client: "David Brown",
  service: "Beard Trim",
  date: "2024-01-16",
  time: "3:00 PM",
  status: "no-show" as const,
  payment: "pending" as const,
  amount: 35
}];

const mockClients = [{
  id: "1",
  name: "Sarah Johnson",
  email: "sarah.j@email.com",
  phone: "+1 (555) 123-4567",
  status: "active" as const,
  bookingCount: 12,
  lastVisit: "2024-01-10",
  preferences: "Prefers morning appointments",
  totalSpent: 1240
}, {
  id: "2",
  name: "Michael Chen",
  email: "m.chen@email.com",
  phone: "+1 (555) 234-5678",
  status: "active" as const,
  bookingCount: 8,
  lastVisit: "2024-01-08",
  preferences: "Allergic to certain hair products",
  totalSpent: 890
}, {
  id: "3",
  name: "Emma Wilson",
  email: "emma.w@email.com",
  phone: "+1 (555) 345-6789",
  status: "inactive" as const,
  bookingCount: 3,
  lastVisit: "2023-12-15",
  preferences: "Prefers female stylists",
  totalSpent: 320
}];

const mockPayments = [{
  id: "1",
  date: "2024-01-15",
  client: "Sarah Johnson",
  amount: 85,
  method: "Credit Card",
  status: "completed" as const
}, {
  id: "2",
  date: "2024-01-15",
  client: "Michael Chen",
  amount: 150,
  method: "Cash",
  status: "completed" as const
}, {
  id: "3",
  date: "2024-01-14",
  client: "Emma Wilson",
  amount: 45,
  method: "Credit Card",
  status: "refunded" as const
}];


export interface BusinessDashboardProps {
  businessName?: string;
  userAvatar?: string;
  userName?: string;
}

// Sidebar Component
const Sidebar = ({
  className,
  activeTab,
  setActiveTab,
  setSidebarOpen,
  businessName
}: {
  className?: string;
  activeTab: string;
  setActiveTab: (tab: string) => void;
  setSidebarOpen: (open: boolean) => void;
  businessName: string;
}) => {
  const sidebarItems = [{
    id: "bookings",
    label: "Bookings",
    icon: Calendar
  }, {
    id: "clients",
    label: "Clients",
    icon: Users
  }, {
    id: "walk-in",
    label: "Walk-In",
    icon: UserPlus
  }, {
    id: "payments",
    label: "Payments",
    icon: CreditCard
  }, {
    id: "settings",
    label: "Settings",
    icon: Settings
  }];

  return (
    <aside className={cn("bg-card border-r border-border", className)}>
      <nav className="p-6 space-y-2">
        <header className="mb-8">
          <h2 className="text-lg font-semibold text-foreground">{businessName}</h2>
          <p className="text-sm text-muted-foreground">Business Dashboard</p>
        </header>
        
        {sidebarItems.map(item => {
          const Icon = item.icon;
          return (
            <Button
              key={item.id}
              variant={activeTab === item.id ? "default" : "ghost"}
              className={cn(
                "w-full justify-start gap-3 h-11 min-h-[44px]",
                activeTab === item.id && "bg-primary text-primary-foreground"
              )}
              onClick={() => {
                setActiveTab(item.id);
                setSidebarOpen(false);
              }}
            >
              <Icon className="h-5 w-5" />
              <span>{item.label}</span>
            </Button>
          );
        })}
      </nav>
    </aside>
  );
};

// Mobile Tab Bar Component
const MobileTabBar = ({
  activeTab,
  setActiveTab
}: {
  activeTab: string;
  setActiveTab: (tab: string) => void;
}) => {
  const tabItems = [{
    id: "bookings",
    label: "Bookings",
    icon: Calendar
  }, {
    id: "clients",
    label: "Clients",
    icon: Users
  }, {
    id: "walk-in",
    label: "Walk-In",
    icon: UserPlus
  }, {
    id: "payments",
    label: "Payments",
    icon: CreditCard
  }, {
    id: "settings",
    label: "Settings",
    icon: Settings
  }];

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border md:hidden">
      <div className="flex items-center justify-around px-2 py-2">
        {tabItems.map(item => {
          const Icon = item.icon;
          const isActive = activeTab === item.id;
          return (
            <Button
              key={item.id}
              variant="ghost"
              className={cn(
                "flex flex-col items-center gap-1 h-auto py-2 px-3 min-h-[60px] flex-1",
                isActive && "bg-primary/10 text-primary"
              )}
              onClick={() => setActiveTab(item.id)}
            >
              <Icon className={cn("h-5 w-5", isActive && "text-primary")} />
              <span className={cn("text-xs font-medium", isActive && "text-primary")}>
                {item.label}
              </span>
            </Button>
          );
        })}
      </div>
    </nav>
  );
};

export default function BusinessDashboard({
  businessName = "Bella Salon & Spa",
  userAvatar = "",
  userName = "Alex Rivera"
}: BusinessDashboardProps) {
  const [activeTab, setActiveTab] = useState("bookings");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState<typeof mockClients[0] | null>(null);
  const [walkInForm, setWalkInForm] = useState({
    name: "",
    contact: "",
    service: "",
    time: ""
  });

  const getStatusBadge = (status: string) => {
    const variants = {
      confirmed: "bg-blue-100 text-blue-700 border-blue-200",
      completed: "bg-green-100 text-green-700 border-green-200",
      cancelled: "bg-red-100 text-red-700 border-red-200",
      "no-show": "bg-orange-100 text-orange-700 border-orange-200"
    };
    const icons = {
      confirmed: CheckCircle,
      completed: CheckCircle,
      cancelled: XCircle,
      "no-show": AlertCircle
    };
    const Icon = icons[status as keyof typeof icons];
    return (
      <Badge className={cn("flex items-center gap-1", variants[status as keyof typeof variants])}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1).replace("-", " ")}
      </Badge>
    );
  };

  const getPaymentBadge = (status: string) => {
    const variants = {
      paid: "bg-green-100 text-green-700 border-green-200",
      pending: "bg-yellow-100 text-yellow-700 border-yellow-200",
      refunded: "bg-gray-100 text-gray-700 border-gray-200"
    };
    return (
      <Badge className={cn("text-xs", variants[status as keyof typeof variants])}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };


  const handleWalkInSubmit = () => {
    console.log("Walk-in booking:", walkInForm);
    setWalkInForm({
      name: "",
      contact: "",
      service: "",
      time: ""
    });
  };

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="p-0 w-72">
          <Sidebar
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            setSidebarOpen={setSidebarOpen}
            businessName={businessName}
          />
        </SheetContent>
      </Sheet>

      <div className="flex">
        {/* Desktop Sidebar - Hidden on mobile */}
        <Sidebar
          className="hidden lg:block w-72 min-h-screen"
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          setSidebarOpen={setSidebarOpen}
          businessName={businessName}
        />
        
        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          {/* Top Bar */}
          <header className="sticky top-0 z-40 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border">
            <div className="flex items-center justify-between px-4 md:px-6 py-4">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden min-h-[44px] min-w-[44px]"
                  onClick={() => setSidebarOpen(true)}
                >
                  <Menu className="h-5 w-5" />
                </Button>
                
                {/* Business name hidden on mobile */}
                <div className="hidden lg:block">
                  <h1 className="text-2xl font-bold text-foreground">{businessName}</h1>
                  <p className="text-sm text-muted-foreground">Manage your business operations</p>
                </div>
              </div>
              
              <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]">
                  <Bell className="h-5 w-5" />
                </Button>
                
                <div className="flex items-center gap-3">
                  <div className="text-right hidden sm:block">
                    <p className="text-sm font-medium text-foreground">{userName}</p>
                    <p className="text-xs text-muted-foreground">Administrator</p>
                  </div>
                  <Avatar className="h-10 w-10">
                    <AvatarImage src={userAvatar} alt={userName} />
                    <AvatarFallback>{userName.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                  </Avatar>
                </div>
              </div>
            </div>
          </header>

          {/* Content Area */}
          <div className="px-2 py-3 md:p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.2 }}
              >
                {/* Bookings Tab */}
                {activeTab === "bookings" && (
                  <section>
                    <div className="flex flex-col gap-4 mb-6">
                      <div>
                        <h2 className="text-base md:text-3xl font-bold tracking-tight">Bookings</h2>
                        <p className="text-muted-foreground text-sm md:text-base">Manage your appointments and schedules</p>
                      </div>
                      <div className="flex flex-col md:flex-row items-stretch md:items-center gap-2">
                        <Button variant="outline" size="icon" className="min-h-[44px] min-w-[44px] md:w-auto">
                          <Filter className="h-4 w-4" />
                        </Button>
                        <Button className="min-h-[44px]">
                          <Plus className="h-4 w-4 mr-2" />
                          New Booking
                        </Button>
                      </div>
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
                      <Table className="min-w-[600px]">
                        <TableHeader>
                          <TableRow className="whitespace-nowrap">
                            <TableHead className="text-sm p-2 md:p-4">Client</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Service</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Time</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Status</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Payment</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Amount</TableHead>
                            <TableHead className="text-right text-sm p-2 md:p-4">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mockBookings.map(booking => (
                            <TableRow key={booking.id} className="whitespace-nowrap">
                              <TableCell className="font-medium text-sm p-2 md:p-4">{booking.client}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{booking.service}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{booking.time}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{getStatusBadge(booking.status)}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{getPaymentBadge(booking.payment)}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">${booking.amount}</TableCell>
                              <TableCell className="text-right text-sm p-2 md:p-4">
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem>
                                      <Edit className="h-4 w-4 mr-2" />
                                      Reschedule
                                    </DropdownMenuItem>
                                    <DropdownMenuItem>
                                      <XCircle className="h-4 w-4 mr-2" />
                                      Cancel
                                    </DropdownMenuItem>
                                    <DropdownMenuItem>
                                      <MessageSquare className="h-4 w-4 mr-2" />
                                      Add Note
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </section>
                )}

                {/* Clients Tab */}
                {activeTab === "clients" && (
                  <section>
                    <div className="flex flex-col gap-4 mb-6">
                      <div>
                        <h2 className="text-base md:text-3xl font-bold tracking-tight">Clients</h2>
                        <p className="text-muted-foreground text-sm md:text-base">Manage your client relationships</p>
                      </div>
                      <Button className="min-h-[44px] w-full md:w-auto">
                        <Plus className="h-4 w-4 mr-2" />
                        Add Client
                      </Button>
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
                      <Table className="min-w-[600px]">
                        <TableHeader>
                          <TableRow className="whitespace-nowrap">
                            <TableHead className="text-sm p-2 md:p-4">Name</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Contact</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Status</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Bookings</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Last Visit</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Total Spent</TableHead>
                            <TableHead className="text-right text-sm p-2 md:p-4">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mockClients.map(client => (
                            <TableRow key={client.id} className="whitespace-nowrap">
                              <TableCell className="font-medium text-sm p-2 md:p-4">{client.name}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">
                                <div className="space-y-1">
                                  <div className="flex items-center gap-1 text-sm">
                                    <Mail className="h-3 w-3" />
                                    {client.email}
                                  </div>
                                  <div className="flex items-center gap-1 text-sm">
                                    <Phone className="h-3 w-3" />
                                    {client.phone}
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="text-sm p-2 md:p-4">
                                <Badge variant={client.status === "active" ? "default" : "secondary"}>
                                  {client.status}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{client.bookingCount}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{client.lastVisit}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">${client.totalSpent}</TableCell>
                              <TableCell className="text-right text-sm p-2 md:p-4">
                                <Drawer>
                                  <DrawerTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      onClick={() => setSelectedClient(client)}
                                      className="min-h-[44px] min-w-[44px]"
                                    >
                                      <Eye className="h-4 w-4" />
                                    </Button>
                                  </DrawerTrigger>
                                  <DrawerContent>
                                    <DrawerHeader>
                                      <DrawerTitle>{selectedClient?.name}</DrawerTitle>
                                      <DrawerDescription>Client Profile & History</DrawerDescription>
                                    </DrawerHeader>
                                    <div className="p-6 space-y-6">
                                      <div className="grid grid-cols-1 gap-6">
                                        <div>
                                          <h4 className="font-semibold mb-3">Contact Information</h4>
                                          <div className="space-y-2 text-sm">
                                            <div className="flex items-center gap-2">
                                              <Mail className="h-4 w-4" />
                                              {selectedClient?.email}
                                            </div>
                                            <div className="flex items-center gap-2">
                                              <Phone className="h-4 w-4" />
                                              {selectedClient?.phone}
                                            </div>
                                          </div>
                                        </div>
                                        <div>
                                          <h4 className="font-semibold mb-3">Preferences</h4>
                                          <p className="text-sm text-muted-foreground">
                                            {selectedClient?.preferences}
                                          </p>
                                        </div>
                                      </div>
                                      <div className="flex items-center justify-between">
                                        <Label htmlFor="opt-out">Marketing Communications</Label>
                                        <Switch id="opt-out" />
                                      </div>
                                    </div>
                                  </DrawerContent>
                                </Drawer>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </section>
                )}

                {/* Walk-In Tab */}
                {activeTab === "walk-in" && (
                  <section>
                    <div className="mb-6">
                      <h2 className="text-base md:text-3xl font-bold tracking-tight">Walk-In Booking</h2>
                      <p className="text-muted-foreground text-sm md:text-base">Quick booking for walk-in clients</p>
                    </div>

                    <Card className="max-w-2xl shadow-sm md:shadow-none">
                      <CardHeader className="px-2 py-3 md:p-6">
                        <CardTitle className="text-base md:text-lg">New Walk-In Client</CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-6 px-2 py-3 md:p-6">
                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="walk-in-name">Client Name</Label>
                            <Input
                              id="walk-in-name"
                              placeholder="Enter client name"
                              value={walkInForm.name}
                              onChange={e => setWalkInForm(prev => ({ ...prev, name: e.target.value }))}
                              className="min-h-[44px]"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="walk-in-contact">Contact</Label>
                            <Input
                              id="walk-in-contact"
                              placeholder="Phone or email"
                              value={walkInForm.contact}
                              onChange={e => setWalkInForm(prev => ({ ...prev, contact: e.target.value }))}
                              className="min-h-[44px]"
                            />
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="walk-in-service">Service</Label>
                            <Select
                              value={walkInForm.service}
                              onValueChange={value => setWalkInForm(prev => ({ ...prev, service: value }))}
                            >
                              <SelectTrigger className="min-h-[44px]">
                                <SelectValue placeholder="Select service" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="haircut">Hair Cut & Style</SelectItem>
                                <SelectItem value="color">Color Treatment</SelectItem>
                                <SelectItem value="manicure">Manicure</SelectItem>
                                <SelectItem value="beard">Beard Trim</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="walk-in-time">Preferred Time</Label>
                            <Select
                              value={walkInForm.time}
                              onValueChange={value => setWalkInForm(prev => ({ ...prev, time: value }))}
                            >
                              <SelectTrigger className="min-h-[44px]">
                                <SelectValue placeholder="Select time" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="now">Now</SelectItem>
                                <SelectItem value="30min">In 30 minutes</SelectItem>
                                <SelectItem value="1hour">In 1 hour</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        <Button onClick={handleWalkInSubmit} className="w-full min-h-[44px]">
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Confirm Walk-In Booking
                        </Button>
                      </CardContent>
                    </Card>
                  </section>
                )}

                {/* Payments Tab */}
                {activeTab === "payments" && (
                  <section>
                    <div className="mb-6">
                      <h2 className="text-base md:text-3xl font-bold tracking-tight">Payments</h2>
                      <p className="text-muted-foreground text-sm md:text-base">Track payment history and transactions</p>
                    </div>

                    <div className="overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
                      <Table className="min-w-[600px]">
                        <TableHeader>
                          <TableRow className="whitespace-nowrap">
                            <TableHead className="text-sm p-2 md:p-4">Date</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Client</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Amount</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Method</TableHead>
                            <TableHead className="text-sm p-2 md:p-4">Status</TableHead>
                            <TableHead className="text-right text-sm p-2 md:p-4">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {mockPayments.map(payment => (
                            <TableRow key={payment.id} className="whitespace-nowrap">
                              <TableCell className="text-sm p-2 md:p-4">{payment.date}</TableCell>
                              <TableCell className="font-medium text-sm p-2 md:p-4">{payment.client}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">
                                <div className="flex items-center gap-1">
                                  <DollarSign className="h-4 w-4" />
                                  {payment.amount}
                                </div>
                              </TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{payment.method}</TableCell>
                              <TableCell className="text-sm p-2 md:p-4">{getPaymentBadge(payment.status)}</TableCell>
                              <TableCell className="text-right text-sm p-2 md:p-4">
                                <Button variant="ghost" size="icon" className="min-h-[44px] min-w-[44px]">
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </section>
                )}

                {/* Settings Tab */}
                {activeTab === "settings" && (
                  <section>
                    <div className="mb-6">
                      <h2 className="text-base md:text-3xl font-bold tracking-tight">Settings</h2>
                      <p className="text-muted-foreground text-sm md:text-base">Configure your business preferences</p>
                    </div>

                    <div className="grid grid-cols-1 gap-6">
                      <Card className="shadow-sm md:shadow-none">
                        <CardHeader className="px-2 py-3 md:p-6">
                          <CardTitle className="text-base md:text-lg">Business Information</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 px-2 py-3 md:p-6">
                          <div className="space-y-2">
                            <Label htmlFor="business-name">Business Name</Label>
                            <Input id="business-name" defaultValue={businessName} className="min-h-[44px]" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="business-address">Address</Label>
                            <Textarea id="business-address" placeholder="Enter business address" className="min-h-[88px]" />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="business-phone">Phone</Label>
                            <Input id="business-phone" placeholder="+1 (555) 123-4567" className="min-h-[44px]" />
                          </div>
                        </CardContent>
                      </Card>

                      <Card className="shadow-sm md:shadow-none">
                        <CardHeader className="px-2 py-3 md:p-6">
                          <CardTitle className="text-base md:text-lg">Notification Preferences</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4 px-2 py-3 md:p-6">
                          <div className="flex items-center justify-between min-h-[44px]">
                            <Label htmlFor="email-notifications">Email Notifications</Label>
                            <Switch id="email-notifications" />
                          </div>
                          <div className="flex items-center justify-between min-h-[44px]">
                            <Label htmlFor="sms-notifications">SMS Notifications</Label>
                            <Switch id="sms-notifications" />
                          </div>
                          <div className="flex items-center justify-between min-h-[44px]">
                            <Label htmlFor="booking-reminders">Booking Reminders</Label>
                            <Switch id="booking-reminders" defaultChecked />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  </section>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>
      </div>

      {/* Mobile Tab Bar */}
      <MobileTabBar activeTab={activeTab} setActiveTab={setActiveTab} />
    </div>
  );
}
